import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where, addDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  Card,
  CardMedia,
  CardContent,
  Rating,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useRef } from 'react';
export default function Home({ cart = [], setCart, companyType = '' }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyCode = searchParams.get('code');
  const unitCode = searchParams.get('unit');
  const tableId = searchParams.get('id');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [recommendedItems, setRecommendedItems] = useState([]);
  const [mostSoldItems, setMostSoldItems] = useState([]);
  const [cookingInstructions, setCookingInstructions] = useState({});
  const [service, setService] = useState('');
  const [cartId, setCartId] = useState('');
  
 const [billingPreference, setBillingPreference] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
const isCustomerParam = searchParams.get('isCustomer');
const nameParam = searchParams.get('name');
const phoneParam = searchParams.get('phone');
  const location = useLocation();

const isCustomer =
  location.state?.isCustomer ??
  (isCustomerParam === 'true' ? true : false);

const customerName =
  location.state?.customerName ||
  nameParam ||
  'Guest';

const phone =
  location.state?.phone ||
  phoneParam ||
  sessionStorage.getItem("phone") ||
  null;

  useEffect(() => {
  if (phone) {
    sessionStorage.setItem("phone", phone);
  }
  if (customerName) {
    sessionStorage.setItem("customerName", customerName);
  }
  if (isCustomer !== undefined) {
    sessionStorage.setItem("isCustomer", isCustomer);
  }
}, [phone, customerName, isCustomer]);

useEffect(() => {
  let isMounted = true;

  const fetchBillingPreference = async () => {
    if (!companyCode || !unitCode) {
      setBillingPreference(2);
      setBillingLoading(false);
      return;
    }

    try {
      setBillingLoading(true);

      const q = query(
        collection(db, "preferences"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", unitCode),
        where("preferenceName", "==", "Billing Process")
      );

      const snapshot = await getDocs(q);

      if (!isMounted) return;

      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        setBillingPreference(Number(data.preferenceValue));
        console.log("Billing Preference:", data.preferenceValue);
      } else {
        setBillingPreference(2); // default safe mode
      }

    } catch (error) {
      console.error("Billing preference error:", error);
      setBillingPreference(2);
    } finally {
      if (isMounted) setBillingLoading(false);
    }
  };

  fetchBillingPreference();

  return () => {
    isMounted = false;
  };
}, [companyCode, unitCode]);

const allowWithoutCart =
  billingPreference === 0 || billingPreference === 1;

const isBillingReady =
  billingLoading === false &&
  billingPreference !== undefined &&
  billingPreference !== null;

const isCartRequired =
  billingPreference !== 0 && billingPreference !== 1;

const canProceed =
  tableId &&
  companyCode &&
  unitCode &&
  isBillingReady &&
  (!isCartRequired || cartId);

const hasCreatedCart = useRef(false);
  // Safeguard companyType
  const safeCompanyType = companyType || '';
  console.log('Home: Parameters:', { companyType, safeCompanyType, companyCode, unitCode, tableId });

  const isFoodcourt = safeCompanyType.toLowerCase() === 'foodcourt';
  const isHotel = safeCompanyType.toLowerCase() === 'hotel';
  const itemType = isFoodcourt ? 'Customer' : isHotel ? 'Room' : 'Table';
const serviceParam = searchParams.get('service');
  const generateCartId = () => {
  return (Math.floor(1000 + Math.random() * 9000)).toString();
};

useEffect(() => {
  const storedCartId = sessionStorage.getItem("cartId");
  if (storedCartId) {
    setCartId(storedCartId);
    console.log("Loaded cartId from session:", storedCartId);
  }
}, []);
  // Construct docId
  const docId = companyCode && unitCode && tableId
    ? `${companyCode}_${unitCode}_${tableId}`
    : null;
  const tableRef = docId ? doc(db, 'tables', docId) : null;

  const addToCart = (item, quantity = 1, instructions = '') => {
    const validQuantity = isNaN(quantity) ? 1 : quantity;
    const existingItem = Array.isArray(cart) ? cart.find(cartItem => cartItem.id === item.id) : null;
    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.id === item.id
          ? {
              ...cartItem,
              quantity: cartItem.quantity + validQuantity,
              totalPrice: (cartItem.quantity + validQuantity) * item.price,
              cookingInstructions: instructions || cartItem.cookingInstructions || ''
            }
          : cartItem
      ));
    } else {
      setCart([...cart, {
        ...item,
        itemInstanceId: `${item.id}_${Date.now()}_${Math.random()}`,
        quantity: validQuantity,
        totalPrice: validQuantity * item.price,
        cookingInstructions: instructions || ''
      }]);
    }
    setCookingInstructions(prev => ({ ...prev, [item.id]: instructions }));
    console.log('Home: Cart updated:', cart);
  };

  useEffect(() => {
    if (!companyCode || !unitCode || !tableId || !docId) {
      setError(`Please provide a valid ${itemType.toLowerCase()} ID, unit code, and company code.`);
      console.error('Home: Invalid parameters:', { companyCode, unitCode, tableId, docId });
      setLoading(false);
      return;
    }

    const validateTableAndFetchItems = async () => {
      try {
        console.log('Home: Validating table:', { companyCode, unitCode, tableId, docId });

        // Validate table in tables collection
        console.log('Home: Fetching table:', docId);
        const tableDoc = await getDoc(tableRef);
        if (!tableDoc.exists()) {
          setError(`The ${itemType.toLowerCase()} ${tableId} does not exist for unit ${unitCode} and company ${companyCode}. Please scan a valid QR code or contact the staff.`);
          console.error('Home: Table not found:', { docId, unitCode, companyCode });
          return;
        }

        const tableData = tableDoc.data();
        setService(tableData.serviceArea || '');
        console.log('Home: Table data:', { docId, data: tableData });

        if (tableData.companyCode !== companyCode || tableData.unitCode !== unitCode ||
          (serviceParam && tableData.serviceArea !== serviceParam)
        ) {
          setError(`Invalid company code or unit code for ${itemType.toLowerCase()} ${tableId}.`);
          console.error('Home: Code mismatch:', {
            tableCompanyCode: tableData.companyCode,
            queryCompanyCode: companyCode,
            tableUnitCode: tableData.unitCode,
            queryUnitCode: unitCode,
            queryServiceArea: serviceParam,
          });
          return;
        }

        // Set company name from table data
        setCompanyName(tableData.companyName || 'Unknown Company');
        console.log('Home: Company name extracted:', { companyName: tableData.companyName });
const currentService = tableData.serviceArea || '';
   //  prevent duplicate execution
/*if (hasCreatedCart.current) return;
hasCreatedCart.current = true;

const cartQuery = query(
  collection(db, "carts"),
  where("companyCode", "==", companyCode),
  where("unitCode", "==", unitCode),
  where("tableId", "==", tableId),
  where("serviceArea", "==", currentService)
);

const snapshot = await getDocs(cartQuery);
if (!snapshot.empty) {
  const existingCart = snapshot.docs[0];
  const data = existingCart.data();

  // ✅ ensure cartStatus exists
  if (!data.cartStatus) {
    await updateDoc(existingCart.ref, {
      cartStatus: "Empty"
    });
  }

  setCartId(data.cartId);
sessionStorage.setItem("cartId", data.cartId);   // ✅ ADD THIS
  console.log("Existing cart:", data);
} else {
  const newCartId = generateCartId();

  const newCart = {
    cartId: newCartId,
    companyCode,
    unitCode,
    tableId,
    serviceArea: currentService,
    cartStatus: "Empty",
    items: [],
    createdAt: new Date()
  };

  const docRef = await addDoc(collection(db, "carts"), newCart);

  await updateDoc(docRef, {
    id: docRef.id
  });

  setCartId(newCartId);
sessionStorage.setItem("cartId", newCartId);   // ✅ ADD THIS

  console.log("New cart created:", {
    ...newCart,
    id: docRef.id
  });
}*/
        // Fetch menu items for company and unit
        console.log('Home: Fetching menu for:', { companyCode, unitCode });
        const menuQuery = query(
          collection(db, 'companymenu'),
          where('companyCode', '==', companyCode),
          where('unitCode', '==', unitCode)
        );
        const menuSnapshot = await getDocs(menuQuery);
        const menuItems = menuSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('Home: Menu items fetched:', menuItems.length);

        // Filter recommended items
        const highRatedItems = menuItems
          .filter(item => item.averageRating >= 4)
          .sort((a, b) => b.averageRating - a.averageRating)
          .slice(0, 5);
        setRecommendedItems(highRatedItems);
        console.log('Home: Recommended items:', highRatedItems);

        // Fetch most sold items
        console.log('Home: Fetching orders for:', { companyCode, unitCode });
        const ordersQuery = query(
          collection(db, 'orders'),
          where('companyCode', '==', companyCode),
          where('unitCode', '==', unitCode)
        );
        const ordersSnapshot = await getDocs(ordersQuery);
        const orderItems = ordersSnapshot.docs
          .flatMap(doc => doc.data().items || [])
          .map(item => ({ id: item.id, quantity: item.quantity }))
          .filter(item => item.id);

        const itemSalesCount = {};
        orderItems.forEach(item => {
          itemSalesCount[item.id] = (itemSalesCount[item.id] || 0) + (item.quantity || 1);
        });

        const mostSoldItemIds = Object.entries(itemSalesCount)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 4)
          .map(([id]) => id);

        const mostSoldDetails = menuItems.filter(item => mostSoldItemIds.includes(item.id));
        setMostSoldItems(mostSoldDetails);
        console.log('Home: Most sold items:', mostSoldDetails);
      } catch (error) {
        console.error('Home: Error validating table or fetching menu/items:', {
          error: error.message,
          code: error.code,
          companyCode,
          unitCode,
          tableId,
          docId,
        });
        setError(`Failed to validate ${itemType.toLowerCase()} or load menu/items: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    validateTableAndFetchItems();
  }, [companyCode, unitCode, tableId, docId, tableRef, itemType, safeCompanyType]);

  /*const handleViewMenu = () => {
    console.log('Home: Navigating to menu:', { tableId, unitCode, companyCode, docId , service});
    if (!cartId) {
    console.warn("CartId not ready yet");
    return;
  }
    const queryParams = new URLSearchParams();
    if (companyCode) queryParams.set('code', companyCode);
    if (unitCode) queryParams.set('unit', unitCode);
    if (tableId) queryParams.set('id', tableId);
    if (service) queryParams.set('service', service);
    if (cartId) queryParams.set('cartId', cartId);
    const queryString = queryParams.toString();
    console.log("Navigating with cartId:", cartId);
    navigate(`/customer/menu${queryString ? `?${queryString}` : ''}`);
  };*/
/*
 const handleViewMenu = () => {
  console.log("CLICK FIRED ✅");

  const allowWithoutCart =
    billingPreference === 0 || billingPreference === 1;

  // ONLY block if cart is required but missing
  if (!allowWithoutCart && !cartId) {
    console.warn("CartId not ready yet");
    return;
  }

  const queryParams = new URLSearchParams({
    code: companyCode || "",
    unit: unitCode || "",
    id: tableId || "",
    service: service || "",
  });

  // 🚨 IMPORTANT: only attach cartId if required
  if (!allowWithoutCart && cartId) {
    queryParams.set("cartId", cartId);
  }

  console.log("Navigating to:", `/customer/menu?${queryParams.toString()}`);

  navigate(`/customer/menu?${queryParams.toString()}`);
};*/

const handleViewMenu = () => {
  console.log("CLICK FIRED ✅");

  if (!service) {
    console.warn("Service not ready yet");
    return;
  }

  if (!allowWithoutCart && !cartId) {
    console.warn("CartId not ready yet");
    return;
  }

  const queryParams = new URLSearchParams({
    code: companyCode || "",
    unit: unitCode || "",
    id: tableId || "",
    service: service || "",
  });

  if (!allowWithoutCart && cartId) {
    queryParams.set("cartId", cartId);
  }

  navigate(`/customer/menu?${queryParams.toString()}`);
};
  const handleSearch = () => {
    console.log('Home: Searching for:', searchQuery);
    // Add search functionality if needed
  };

console.log("canProceed:", canProceed);
console.log("service:", service);
console.log("cartId:", cartId);
console.log("isBillingReady:", isBillingReady);
console.log("isCartRequired:", isCartRequired);
  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#e4b7ec',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1,
        }}
        data-component-name="loading-container"
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress
            sx={{
              color: '#93c5fd',
              mb: 2,
            }}
            data-component-name="loading-spinner"
          />
          <Typography
            variant="h6"
            sx={{ color: '#4b5563', fontWeight: 500 }}
            data-component-name="loading-text"
          >
            Loading...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f9fafb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1,
        }}
        data-component-name="error-container"
      >
        <Box sx={{ textAlign: 'center', maxWidth: 448, px: 4 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 700, color: '#1f2937', mb: 4 }}
            data-component-name="error-title"
          >
            Error
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: '#ef4444', mb: 6 }}
            data-component-name="error-message"
          >
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{
              bgcolor: '#93c5fd',
              color: '#ffffff',
              px: 6,
              py: 1.5,
              fontWeight: 500,
              borderRadius: 8,
              textTransform: 'none',
              '&:hover': {
                bgcolor: '#60a5fa',
              },
            }}
            data-component-name="error-retry-button"
          >
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{ minHeight: '100vh', bgcolor: '#f9fafb', position: 'relative' }}
      data-component-name="home-container"
    >
      {/* Hero Section */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgb(126,114,177), rgb(228,168,240))',
          zIndex: 1,
        }}
        data-component-name="hero-section"
      >
        {/* Animated Emojis */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '20%', md: '20%' },
            left: { xs: '5%', md: '10%' },
            fontSize: { xs: '3rem', md: '6xl' },
            animation: 'float 3s ease-in-out infinite',
            opacity: 0.5,
          }}
          data-component-name="emoji-pizza"
        >
          🍕
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '30%', md: '32%' },
            right: { xs: '5%', md: '20%' },
            fontSize: { xs: '2rem', md: '4xl' },
            animation: 'float 3s ease-in-out infinite',
            animationDelay: '1s',
            opacity: 0.5,
          }}
          data-component-name="emoji-burger"
        >
          🍔
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: '20%', md: '20%' },
            left: { xs: '25%', md: '25%' },
            fontSize: { xs: '2.5rem', md: '5xl' },
            animation: 'float 3s ease-in-out infinite',
            animationDelay: '2s',
            opacity: 0.5,
          }}
          data-component-name="emoji-icecream"
        >
          🍦
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: 1152,
            mx: 'auto',
            px: 2,
            textAlign: 'center',
            zIndex: 1,
          }}
          data-component-name="hero-content"
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '2.25rem', md: '3.75rem' },
              fontWeight: 700,
              color: '#ffffff',
              mb: 4,
              animation: 'slide-up 0.5s ease-out',
            }}
            data-component-name="hero-title"
          >
            Welcome to {companyName || (isFoodcourt ? 'Our Foodcourt' : isHotel ? 'Our Hotel' : 'Our Restaurant')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.125rem', md: '1.25rem' },
              color: '#ffffff',
              mb: 2,
              opacity: 0.9,
            }}
            data-component-name="hero-subtitle"
          >
            You are at {itemType.toLowerCase()} {tableId} in unit {unitCode}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '1rem', md: '1.125rem' },
              color: '#ffffff',
              mb: 8,
              opacity: 0.8,
            }}
            data-component-name="hero-description"
          >
            Discover delicious meals and place your order with ease!
          </Typography>

          {/* Search Bar */}
          {/* <Box
            sx={{
              width: '100%',
              maxWidth: 640,
              mx: 'auto',
              bgcolor: 'rgba(255, 255, 255, 0.85)',
              borderRadius: 12,
              p: 2,
              mb: 6,
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
            data-component-name="search-bar"
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, px: 4 }}>
                <SearchIcon sx={{ color: '#4b5563', width: 20, height: 20 }} data-component-name="search-icon" />
                <TextField
                  type="text"
                  placeholder="Search for restaurants, dishes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  fullWidth
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      color: '#1f2937',
                      '& .MuiInputBase-input::placeholder': {
                        color: '#6b7280',
                        opacity: 1,
                      },
                      py: 1.5,
                    },
                  }}
                  sx={{ bgcolor: 'transparent' }}
                  data-component-name="search-input"
                />
              </Box>
              <Button
                onClick={handleSearch}
                sx={{
                  bgcolor: '#735DA5',
                  color: '#ffffff',
                  px: 8,
                  py: 1.5,
                  borderRadius: 12,
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#a583f2',
                  },
                }}
                data-component-name="search-button"
              >
                Search
              </Button>
            </Box>
          </Box> */}
          <Button
            onClick={handleViewMenu}
            //disabled={!tableId || !companyCode || !unitCode}
            //disabled={!tableId || !companyCode || !unitCode || !cartId}
             /*disabled={
    !tableId ||
    !companyCode ||
    !unitCode ||
    !isBillingReady ||
    (!allowWithoutCart && !cartId)
  }*/
disabled={!canProceed}
            sx={{
              bgcolor: '#ffffff',
              color: '#735DA5',
              px: 8,
              py: 1.5,
              borderRadius: 8,
              fontWeight: 700,
              fontSize: '1.125rem',
              textTransform: 'none',
              '&:hover': {
                bgcolor: '#e5e7eb',
              },
            }}
            data-component-name="view-menu-button"
          >
            View Full Menu
          </Button>
        </Box>
      </Box>

      {/* Recommended for You Section */}
      <Box
        component="section"
        sx={{
          py: 12,
          px: 2,
          bgcolor: '#e5e7eb',
          zIndex: 1,
        }}
        data-component-name="recommended-for-you"
      >
        <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', textAlign: 'center', color: '#1f2937' }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, fontSize: '2.25rem' }}
            data-component-name="recommended-title"
          >
            Recommended for You
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontSize: '1.25rem', mb: 8, opacity: 0.9 }}
            data-component-name="recommended-description"
          >
            Personalized picks based on your preferences!
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              '-webkit-overflow-scrolling': 'touch',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              pb: 2,
            }}
            data-component-name="recommended-cards"
          >
            {recommendedItems.map((item, index) => (
              <Card
                key={index}
                sx={{
                  width: 300,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  flexShrink: 0,
                  mb: 2,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                  },
                }}
                data-component-name={`recommended-card-${index}`}
              >
                <CardMedia
                  component="img"
                  sx={{ height: 180, objectFit: 'cover' }}
                  image={item.imageUrl || '/placeholder.png'}
                  alt={item.name}
                />
                <CardContent sx={{ p: 2, flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                    {item.description || 'No description available'}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#1f2937' }}>
                      ₹{item.price}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Rating value={item.averageRating} precision={0.1} readOnly sx={{ mr: 1 }} />
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        ({item.averageRating})
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
                <Box sx={{ p: 1 }}>
                  <Button
                    variant="contained"
                    onClick={(e) => { e.stopPropagation(); addToCart(item, 1, ''); }}
                    sx={{
                      bgcolor: '#735DA5',
                      color: '#ffffff',
                      px: 8,
                      py: 1.5,
                      borderRadius: 12,
                      fontWeight: 500,
                      mb: 2,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: '#a583f2',
                      },
                    }}
                    data-component-name={`add-to-cart-button-${index}`}
                  >
                    Add to Cart
                  </Button>
                </Box>
              </Card>
            ))}
          </Box>
          {recommendedItems.length === 0 && (
            <Typography
              variant="h6"
              sx={{ color: '#64748b', textAlign: 'center', mt: 4 }}
              data-component-name="no-recommendations"
            >
              No recommended items available.
            </Typography>
          )}
        </Box>
      </Box>

      {/* Most Sold Items Section */}
      <Box
        component="section"
        sx={{
          py: 12,
          px: 2,
          bgcolor: '#f3f4f6',
          zIndex: 1,
        }}
        data-component-name="most-sold-items"
      >
        <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', textAlign: 'center', color: '#1f2937' }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 4, fontSize: '2.25rem' }}
            data-component-name="most-sold-title"
          >
            Most Sold Items
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontSize: '1.25rem', mb: 8, opacity: 0.9 }}
            data-component-name="most-sold-description"
          >
            Our top picks based on customer favorites!
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 4,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              '-webkit-overflow-scrolling': 'touch',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              pb: 2,
            }}
            data-component-name="most-sold-cards"
          >
            {mostSoldItems.map((item, index) => (
              <Card
                key={index}
                sx={{
                  width: 300,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  flexShrink: 0,
                  mb: 1,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                  },
                }}
                data-component-name={`most-sold-card-${index}`}
              >
                <CardMedia
                  component="img"
                  sx={{ height: 180, objectFit: 'cover' }}
                  image={item.imageUrl || '/placeholder.png'}
                  alt={item.name}
                />
                <CardContent sx={{ p: 2, flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                    {item.description || 'No description available'}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#1f2937' }}>
                      ₹{item.price}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Rating value={item.averageRating} precision={0.1} readOnly sx={{ mr: 1 }} />
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        ({item.averageRating})
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
                <Box sx={{ p: 1 }}>
                  <Button
                    variant="contained"
                    onClick={(e) => { e.stopPropagation(); addToCart(item, 1, ''); }}
                    sx={{
                      bgcolor: '#735DA5',
                      color: '#ffffff',
                      px: 8,
                      py: 1.5,
                      borderRadius: 12,
                      fontWeight: 500,
                      mb: 2,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: '#a583f2',
                      },
                    }}
                    data-component-name={`add-to-cart-button-${index + 4}`}
                  >
                    Add to Cart
                  </Button>
                </Box>
              </Card>
            ))}
          </Box>
          {mostSoldItems.length === 0 && (
            <Typography
              variant="h6"
              sx={{ color: '#64748b', textAlign: 'center', mt: 4 }}
              data-component-name="no-most-sold"
            >
              No most sold items available.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}